import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PiggyBank, Plus, Pencil, ChevronDown, ChevronRight, X, PauseCircle, Users, RotateCcw, LogOut, CheckCircle2, XCircle, FileText, ShieldCheck } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { EscrowCorrectionModal } from '../components/EscrowCorrectionModal';
import { toast } from '../components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  suspended: 'bg-slate-100 text-slate-600 border-slate-200',
  completed: 'bg-blue-100 text-blue-800 border-blue-200',
};

const EscrowManagement = () => {
  const [configs, setConfigs] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // config being edited or null
  const [form, setForm] = useState({ driver_id: '', total_required: '', per_period_amount: '', status: 'active', notes: '' });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null); // driver_id whose ledger is open
  const [ledgerByDriver, setLedgerByDriver] = useState({});
  const [correcting, setCorrecting] = useState(null); // { row, driverName }
  const [showBulk, setShowBulk] = useState(false);
  const [bulkForm, setBulkForm] = useState({ total_required: '', per_period_amount: '', status: 'active', notes: '' });
  const [bulkSelected, setBulkSelected] = useState([]);
  const [savingBulk, setSavingBulk] = useState(false);
  const [threshold, setThreshold] = useState(500);
  const [editingThreshold, setEditingThreshold] = useState(false);
  const [thresholdInput, setThresholdInput] = useState('');
  const [approvals, setApprovals] = useState([]);
  const [showBulkRefund, setShowBulkRefund] = useState(false);
  const [bulkRefundSelected, setBulkRefundSelected] = useState([]);
  const [savingBulkRefund, setSavingBulkRefund] = useState(false);
  const [settlement, setSettlement] = useState(null);
  const [settling, setSettling] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundForm, setRefundForm] = useState({ amount: '', reason: 'overpayment', notes: '' });
  const [savingRefund, setSavingRefund] = useState(false);
  const [refundsByDriver, setRefundsByDriver] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, dRes, sRes, aRes] = await Promise.all([
        axios.get(`${API}/escrow/configs`, { withCredentials: true }),
        axios.get(`${API}/drivers`, { withCredentials: true }),
        axios.get(`${API}/escrow/settings`, { withCredentials: true }),
        axios.get(`${API}/escrow/refunds?status=awaiting_approval`, { withCredentials: true }),
      ]);
      setConfigs(cRes.data || []);
      setDrivers((dRes.data || []).filter((d) => d.status === 'active'));
      setThreshold(sRes.data?.approval_threshold ?? 500);
      setApprovals(aRes.data || []);
    } catch (err) {
      toast.error('Failed to load escrow configs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setForm({ driver_id: '', total_required: '', per_period_amount: '', status: 'active', notes: '' });
    setShowModal(true);
  };

  const openEdit = (cfg) => {
    setEditing(cfg);
    setForm({
      driver_id: cfg.driver_id,
      total_required: String(cfg.total_required),
      per_period_amount: String(cfg.per_period_amount),
      status: cfg.status || 'active',
      notes: cfg.notes || '',
    });
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.driver_id) { toast.error('Select a driver'); return; }
    const total = parseFloat(form.total_required);
    const per = parseFloat(form.per_period_amount);
    if (!(per > 0)) { toast.error('Per-period amount must be greater than 0'); return; }
    if (!(total >= 0)) { toast.error('Total required must be 0 or more'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/escrow/config/${form.driver_id}`, {
        total_required: total,
        per_period_amount: per,
        status: form.status,
        notes: form.notes || null,
      }, { withCredentials: true });
      toast.success(editing ? 'Escrow updated' : 'Escrow created');
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save escrow');
    } finally { setSaving(false); }
  };

  const suspend = async (cfg) => {
    if (!window.confirm(`Suspend escrow for ${cfg.driver_name || 'this driver'}? No more will be collected until reactivated.`)) return;
    try {
      await axios.delete(`${API}/escrow/config/${cfg.driver_id}`, { withCredentials: true });
      toast.success('Escrow suspended');
      fetchData();
    } catch (err) { toast.error('Failed to suspend'); }
  };

  const refund = (cfg) => {
    setRefundTarget(cfg);
    setRefundForm({ amount: '', reason: 'overpayment', notes: '' });
  };

  const submitRefund = async (e) => {
    e.preventDefault();
    const amt = parseFloat(refundForm.amount);
    if (!(amt > 0)) { toast.error('Enter a positive amount'); return; }
    if (amt > refundTarget.held_balance + 0.005) { toast.error(`Max refundable is ${fmtMoney(refundTarget.held_balance)}`); return; }
    setSavingRefund(true);
    try {
      const res = await axios.post(`${API}/escrow/config/${refundTarget.driver_id}/refund`, {
        amount: amt, reason: refundForm.reason, notes: refundForm.notes || null,
      }, { withCredentials: true });
      toast.success(res.data?.needs_approval ? 'Refund queued — awaiting a second admin\'s approval' : 'Refund queued for next payroll');
      setRefundTarget(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to refund');
    } finally { setSavingRefund(false); }
  };

  const openBulk = () => {
    setBulkForm({ total_required: '', per_period_amount: '', status: 'active', notes: '' });
    setBulkSelected([]);
    setShowBulk(true);
  };

  const toggleBulkDriver = (id) => {
    setBulkSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };

  const saveBulk = async (e) => {
    e.preventDefault();
    if (bulkSelected.length === 0) { toast.error('Select at least one driver'); return; }
    const total = parseFloat(bulkForm.total_required);
    const per = parseFloat(bulkForm.per_period_amount);
    if (!(per > 0)) { toast.error('Per-period amount must be greater than 0'); return; }
    if (!(total >= 0)) { toast.error('Total required must be 0 or more'); return; }
    setSavingBulk(true);
    try {
      const res = await axios.post(`${API}/escrow/configs/bulk`, {
        driver_ids: bulkSelected,
        total_required: total,
        per_period_amount: per,
        status: bulkForm.status,
        notes: bulkForm.notes || null,
      }, { withCredentials: true });
      const { created = 0, updated = 0 } = res.data || {};
      toast.success(`Escrow applied: ${created} created, ${updated} updated`);
      setShowBulk(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply bulk escrow');
    } finally { setSavingBulk(false); }
  };

  const saveThreshold = async () => {
    const val = parseFloat(thresholdInput);
    if (!(val >= 0)) { toast.error('Enter a valid amount'); return; }
    try {
      await axios.put(`${API}/escrow/settings`, { approval_threshold: val }, { withCredentials: true });
      setThreshold(val);
      setEditingThreshold(false);
      toast.success('Approval threshold updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update threshold'); }
  };

  const approveRefund = async (rf) => {
    try {
      await axios.post(`${API}/escrow/refunds/${rf.id}/approve`, {}, { withCredentials: true });
      toast.success('Refund approved — pays out on next payroll');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve'); }
  };

  const rejectRefund = async (rf) => {
    const reason = window.prompt(`Reject refund of ${fmtMoney(rf.amount)} for ${rf.driver_name}? Optional reason:`, '');
    if (reason === null) return;
    try {
      await axios.post(`${API}/escrow/refunds/${rf.id}/reject?reason=${encodeURIComponent(reason || '')}`, {}, { withCredentials: true });
      toast.success('Refund rejected — escrow released');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reject'); }
  };

  const openBulkRefund = () => {
    setBulkRefundSelected([]);
    setShowBulkRefund(true);
  };

  const saveBulkRefund = async (e) => {
    e.preventDefault();
    if (bulkRefundSelected.length === 0) { toast.error('Select at least one driver'); return; }
    setSavingBulkRefund(true);
    try {
      const res = await axios.post(`${API}/escrow/refunds/bulk`, { driver_ids: bulkRefundSelected }, { withCredentials: true });
      const { refunded = 0, awaiting_approval = 0, total_amount = 0 } = res.data || {};
      toast.success(`Refunded ${fmtMoney(total_amount)} — ${refunded} queued, ${awaiting_approval} need approval`);
      setShowBulkRefund(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to bulk refund');
    } finally { setSavingBulkRefund(false); }
  };

  const settle = async (cfg) => {
    if (!window.confirm(`End-of-contract settlement for ${cfg.driver_name}?\nThis nets their held escrow (${fmtMoney(cfg.held_balance)}) against any outstanding deferred debt, queues a refund for the remainder, and closes the escrow.`)) return;
    setSettling(true);
    try {
      const res = await axios.post(`${API}/escrow/config/${cfg.driver_id}/settle`, { notes: null }, { withCredentials: true });
      setSettlement(res.data);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to settle');
    } finally { setSettling(false); }
  };

  const downloadStatement = async (s) => {
    try {
      const res = await axios.get(`${API}/escrow/settlements/${s.id}/statement.pdf`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error('Failed to open statement');
    }
  };

  const blobDownload = async (path, filename, { open = false, type = 'application/pdf' } = {}) => {
    try {
      const res = await axios.get(`${API}${path}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      if (open) {
        window.open(url, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const exportCsv = () => blobDownload('/escrow/refunds/export.csv', 'escrow_refunds.csv', { type: 'text/csv' });
  const downloadAdminStatement = (cfg) => blobDownload(`/escrow/config/${cfg.driver_id}/statement.pdf`, `escrow_statement.pdf`, { open: true });
  const downloadPacket = (s) => blobDownload(`/escrow/settlements/${s.id}/offboarding-packet.zip`, `offboarding.zip`, { type: 'application/zip' });

  const remindApproval = async (rf) => {
    try {
      await axios.post(`${API}/escrow/refunds/${rf.id}/remind`, {}, { withCredentials: true });
      toast.success('Reminder sent to the other admins');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send reminder'); }
  };

  const refundableDrivers = configs.filter((c) => c.held_balance > 0);

  const toggleLedger = async (driverId) => {
    if (expanded === driverId) { setExpanded(null); return; }
    setExpanded(driverId);
    if (!ledgerByDriver[driverId]) {
      try {
        const res = await axios.get(`${API}/escrow/ledger?driver_id=${driverId}`, { withCredentials: true });
        setLedgerByDriver((p) => ({ ...p, [driverId]: res.data || [] }));
      } catch (err) { setLedgerByDriver((p) => ({ ...p, [driverId]: [] })); }
    }
    if (!refundsByDriver[driverId]) {
      try {
        const res = await axios.get(`${API}/escrow/refunds?driver_id=${driverId}`, { withCredentials: true });
        setRefundsByDriver((p) => ({ ...p, [driverId]: res.data || [] }));
      } catch (err) { setRefundsByDriver((p) => ({ ...p, [driverId]: [] })); }
    }
  };

  const availableDrivers = editing
    ? drivers
    : drivers.filter((d) => !configs.some((c) => c.driver_id === d.id));

  return (
    <DashboardLayout>
      <div data-testid="escrow-management-page" className="p-4 md:p-8 max-w-6xl mx-auto">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              <PiggyBank className="w-8 h-8 text-[#002FA7]" /> Escrow Management
            </h1>
            <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Configure recurring escrow deductions per driver. Uncollected amounts defer and carry forward automatically.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div data-testid="threshold-widget" className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-2">
              <ShieldCheck className="w-4 h-4 text-[#002FA7]" />
              {editingThreshold ? (
                <>
                  <span className="text-xs text-slate-500">Approve over $</span>
                  <input data-testid="threshold-input" type="number" min="0" step="1" value={thresholdInput} onChange={(e) => setThresholdInput(e.target.value)} className="w-20 px-2 py-1 border border-slate-300 rounded text-sm" />
                  <button data-testid="threshold-save" onClick={saveThreshold} className="text-xs font-semibold text-[#002FA7] hover:underline">Save</button>
                  <button onClick={() => setEditingThreshold(false)} className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>
                </>
              ) : (
                <>
                  <span className="text-xs text-slate-600">Refunds ≥ <span className="font-bold text-slate-900">{fmtMoney(threshold)}</span> need approval</span>
                  <button data-testid="threshold-edit" onClick={() => { setThresholdInput(String(threshold)); setEditingThreshold(true); }} className="text-slate-400 hover:text-[#002FA7]"><Pencil className="w-3.5 h-3.5" /></button>
                </>
              )}
            </div>
            <button data-testid="export-refunds-csv" onClick={exportCsv} className="flex items-center gap-2 bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-md font-semibold hover:bg-slate-50 transition-all">
              <FileText className="w-4 h-4" /> Export CSV
            </button>
            <button data-testid="bulk-refund-button" onClick={openBulkRefund} className="flex items-center gap-2 bg-white border border-emerald-500 text-emerald-700 px-4 py-2 rounded-md font-semibold hover:bg-emerald-50 transition-all">
              <RotateCcw className="w-4 h-4" /> Bulk Refund
            </button>
            <button data-testid="bulk-escrow-button" onClick={openBulk} className="flex items-center gap-2 bg-white border border-[#002FA7] text-[#002FA7] px-4 py-2 rounded-md font-semibold hover:bg-blue-50 transition-all">
              <Users className="w-4 h-4" /> Bulk Setup
            </button>
            <button data-testid="add-escrow-button" onClick={openCreate} className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all">
              <Plus className="w-4 h-4" /> Add Escrow
            </button>
          </div>
        </div>

        {approvals.length > 0 && (
          <div data-testid="pending-approvals-panel" className="mb-6 border border-amber-300 bg-amber-50 rounded-md p-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-5 h-5 text-amber-700" />
              <h2 className="text-base font-bold text-amber-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Refunds Awaiting Approval ({approvals.length})</h2>
            </div>
            <div className="space-y-2">
              {approvals.map((rf) => (
                <div key={rf.id} data-testid={`approval-row-${rf.id}`} className="flex items-center justify-between bg-white border border-amber-200 rounded-md px-3 py-2 flex-wrap gap-2">
                  <div className="text-sm">
                    <span className="font-semibold text-slate-900">{rf.driver_name}</span>
                    <span className="text-slate-500"> · </span>
                    <span className="font-bold text-emerald-700">{fmtMoney(rf.amount)}</span>
                    {rf.reason_label && <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-600 bg-slate-100 rounded px-1.5 py-0.5">{rf.reason_label}</span>}
                    {rf.source === 'settlement' && <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">settlement</span>}
                    {rf.notes && <span className="text-xs text-slate-500 ml-2">{rf.notes}</span>}
                    <p className="text-[11px] text-slate-400 mt-0.5">Requested by {rf.requested_by_name || 'admin'}{rf.created_at ? ` · ${new Date(rf.created_at).toLocaleDateString()}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`remind-refund-${rf.id}`} onClick={() => remindApproval(rf)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 border border-slate-300 rounded-md px-2.5 py-1.5 hover:bg-slate-50" title="Nudge other admins">Remind</button>
                    <button data-testid={`approve-refund-${rf.id}`} onClick={() => approveRefund(rf)} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 border border-emerald-300 rounded-md px-2.5 py-1.5 hover:bg-emerald-50"><CheckCircle2 className="w-3.5 h-3.5" /> Approve</button>
                    <button data-testid={`reject-refund-${rf.id}`} onClick={() => rejectRefund(rf)} className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 border border-red-200 rounded-md px-2.5 py-1.5 hover:bg-red-50"><XCircle className="w-3.5 h-3.5" /> Reject</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading…</p>
        ) : configs.length === 0 ? (
          <div data-testid="escrow-empty" className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center">
            <PiggyBank className="w-10 h-10 text-slate-400 mx-auto mb-2" />
            <p className="text-sm font-semibold text-slate-700">No escrow configured yet</p>
            <p className="text-xs text-slate-500 mt-1">Click "Add Escrow" to start collecting an escrow balance from a driver.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-widest text-slate-500 bg-slate-50">
                  <th className="text-left py-3 px-4 font-semibold">Driver</th>
                  <th className="text-right py-3 px-4 font-semibold">Required</th>
                  <th className="text-right py-3 px-4 font-semibold">Per Period</th>
                  <th className="text-right py-3 px-4 font-semibold">Collected</th>
                  <th className="text-right py-3 px-4 font-semibold">Remaining</th>
                  <th className="text-center py-3 px-4 font-semibold">Status</th>
                  <th className="text-right py-3 px-4 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {configs.map((c) => (
                  <React.Fragment key={c.id}>
                    <tr data-testid={`escrow-row-${c.driver_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <button onClick={() => toggleLedger(c.driver_id)} className="flex items-center gap-1.5 text-left" data-testid={`escrow-expand-${c.driver_id}`}>
                          {expanded === c.driver_id ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                          <span className="text-slate-900 font-medium">{c.driver_name || c.driver_email || c.driver_id}</span>
                        </button>
                      </td>
                      <td className="py-3 px-4 text-right text-slate-900">{fmtMoney(c.total_required)}</td>
                      <td className="py-3 px-4 text-right text-slate-700">{fmtMoney(c.per_period_amount)}</td>
                      <td className="py-3 px-4 text-right text-emerald-700 font-semibold">{fmtMoney(c.collected_to_date)}</td>
                      <td className="py-3 px-4 text-right text-orange-700 font-semibold">{fmtMoney(c.remaining)}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-block px-2 py-1 rounded text-[11px] font-semibold border ${STATUS_STYLES[c.status] || STATUS_STYLES.suspended}`}>{c.status}</span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button data-testid={`escrow-edit-${c.driver_id}`} onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Edit"><Pencil className="w-4 h-4" /></button>
                          <button data-testid={`escrow-statement-${c.driver_id}`} onClick={() => downloadAdminStatement(c)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Escrow statement"><FileText className="w-4 h-4" /></button>
                          {c.held_balance > 0 && (
                            <button data-testid={`escrow-refund-${c.driver_id}`} onClick={() => refund(c)} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600" title="Refund"><RotateCcw className="w-4 h-4" /></button>
                          )}
                          {c.status !== 'settled' && (
                            <button data-testid={`escrow-settle-${c.driver_id}`} onClick={() => settle(c)} disabled={settling} className="p-1.5 rounded hover:bg-purple-50 text-purple-600 disabled:opacity-50" title="End-of-contract settlement"><LogOut className="w-4 h-4" /></button>
                          )}
                          {c.status !== 'suspended' && c.status !== 'settled' && (
                            <button data-testid={`escrow-suspend-${c.driver_id}`} onClick={() => suspend(c)} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Suspend"><PauseCircle className="w-4 h-4" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded === c.driver_id && (
                      <tr className="bg-slate-50">
                        <td colSpan={7} className="px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Collection History</p>
                          {(ledgerByDriver[c.driver_id] || []).length === 0 ? (
                            <p className="text-xs text-slate-500">No collection history yet.</p>
                          ) : (
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-slate-500 uppercase tracking-widest">
                                  <th className="text-left py-1">Period</th>
                                  <th className="text-right py-1">Due</th>
                                  <th className="text-right py-1">Collected</th>
                                  <th className="text-right py-1">Deferred</th>
                                  <th className="text-right py-1">Balance After</th>
                                  <th className="text-center py-1">Status</th>
                                  <th className="text-right py-1"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {(ledgerByDriver[c.driver_id] || []).map((r) => (
                                  <tr key={r.id} className="border-t border-slate-200" data-testid={`escrow-ledger-row-${r.id}`}>
                                    <td className="py-1 text-slate-700">
                                      {r.period_start} → {r.period_end}
                                      {r.pending_release && <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800" title="This payroll has not been sent yet — the amount is not applied to the driver's balance until Send payroll" data-testid={`escrow-ledger-pending-${r.id}`}>{r.payroll_status === 'pending_revision' ? 'pending revision' : 'draft — not released'}</span>}
                                    </td>
                                    <td className="py-1 text-right">
                                      {fmtMoney(r.amount_due)}
                                      {r.original && r.original.amount_due !== r.amount_due && <span className="ml-1 text-[10px] text-slate-400 line-through" title={`Originally due ${fmtMoney(r.original.amount_due)}`}>{fmtMoney(r.original.amount_due)}</span>}
                                    </td>
                                    <td className="py-1 text-right text-emerald-700">
                                      {fmtMoney(r.amount_collected)}
                                      {r.original && <span className="ml-1 text-[10px] text-slate-400 line-through" title={`Originally collected ${fmtMoney(r.original.amount_collected)}`}>{fmtMoney(r.original.amount_collected)}</span>}
                                    </td>
                                    <td className="py-1 text-right text-orange-700">{fmtMoney(r.amount_deferred)}</td>
                                    <td className="py-1 text-right">{fmtMoney(r.balance_after)}</td>
                                    <td className="py-1 text-center">
                                      {r.status}
                                      {(r.corrections || []).length > 0 && (
                                        <span className="ml-1 text-[10px] text-slate-500" title={r.corrections.map((x) => `${x.by_name || 'admin'} · ${new Date(x.at).toLocaleString()} · ${fmtMoney(x.old_collected)} → ${fmtMoney(x.new_collected)} · ${x.reason}`).join('\n')} data-testid={`escrow-ledger-corrected-${r.id}`}>
                                          ({r.corrections.length} correction{r.corrections.length > 1 ? 's' : ''}{r.payroll_locked ? ', payroll locked' : ''})
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-1 text-right">
                                      <button type="button" onClick={() => setCorrecting({ row: r, driverName: c.driver_name })}
                                        className="rounded border border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:bg-white" data-testid={`escrow-ledger-correct-${r.id}`}>
                                        Correct
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2 mt-4">Refund History</p>
                          {(refundsByDriver[c.driver_id] || []).length === 0 ? (
                            <p className="text-xs text-slate-500">No refunds yet.</p>
                          ) : (
                            <table className="w-full text-xs" data-testid={`refund-history-${c.driver_id}`}>
                              <thead>
                                <tr className="text-slate-500 uppercase tracking-widest">
                                  <th className="text-left py-1">Amount</th>
                                  <th className="text-left py-1">Reason</th>
                                  <th className="text-center py-1">Status</th>
                                  <th className="text-left py-1">Trail</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(refundsByDriver[c.driver_id] || []).map((rf) => (
                                  <tr key={rf.id} className="border-t border-slate-200">
                                    <td className="py-1 font-semibold text-emerald-700">{fmtMoney(rf.amount)}</td>
                                    <td className="py-1 text-slate-700">{rf.reason_label || rf.notes || '—'}</td>
                                    <td className="py-1 text-center"><span className="text-[10px] uppercase tracking-widest text-slate-600">{rf.status}</span></td>
                                    <td className="py-1 text-slate-500">
                                      Req: {rf.requested_by_name || 'admin'}
                                      {rf.approved_by_name ? ` · Appr: ${rf.approved_by_name}` : ''}
                                      {rf.rejected_by_name ? ` · Rej: ${rf.rejected_by_name}` : ''}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
            <div data-testid="escrow-modal" className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{editing ? 'Edit Escrow' : 'Add Escrow'}</h2>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={save} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Driver</label>
                  <select data-testid="escrow-driver-select" value={form.driver_id} disabled={!!editing} onChange={(e) => setForm({ ...form, driver_id: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white disabled:bg-slate-100">
                    <option value="">Select a driver…</option>
                    {availableDrivers.map((d) => <option key={d.id} value={d.id}>{d.name || d.email}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Total Required ($)</label>
                    <input data-testid="escrow-total-input" type="number" step="0.01" min="0" value={form.total_required} onChange={(e) => setForm({ ...form, total_required: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md" required />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Per Period ($)</label>
                    <input data-testid="escrow-perperiod-input" type="number" step="0.01" min="0.01" value={form.per_period_amount} onChange={(e) => setForm({ ...form, per_period_amount: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md" required />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Status</label>
                  <select data-testid="escrow-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white">
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Notes</label>
                  <textarea data-testid="escrow-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <button data-testid="escrow-save-button" type="submit" disabled={saving} className="bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
                  <button type="button" onClick={() => setShowModal(false)} className="text-sm text-slate-600 hover:text-slate-900">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}
        {showBulk && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowBulk(false)}>
            <div data-testid="escrow-bulk-modal" className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Bulk Escrow Setup</h2>
                <button onClick={() => setShowBulk(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-xs text-slate-500 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Apply the same escrow schedule to multiple drivers at once. Existing configs for selected drivers are updated.</p>
              <form onSubmit={saveBulk} className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Total Required ($)</label>
                    <input data-testid="bulk-total-input" type="number" step="0.01" min="0" value={bulkForm.total_required} onChange={(e) => setBulkForm({ ...bulkForm, total_required: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md" required />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Per Period ($)</label>
                    <input data-testid="bulk-perperiod-input" type="number" step="0.01" min="0.01" value={bulkForm.per_period_amount} onChange={(e) => setBulkForm({ ...bulkForm, per_period_amount: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md" required />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Status</label>
                    <select data-testid="bulk-status-select" value={bulkForm.status} onChange={(e) => setBulkForm({ ...bulkForm, status: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white">
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Drivers ({bulkSelected.length} selected)</label>
                    <button type="button" data-testid="bulk-select-all" onClick={() => setBulkSelected(bulkSelected.length === drivers.length ? [] : drivers.map((d) => d.id))} className="text-xs text-[#002FA7] font-semibold hover:underline">
                      {bulkSelected.length === drivers.length ? 'Clear all' : 'Select all'}
                    </button>
                  </div>
                  <div className="border border-slate-200 rounded-md max-h-56 overflow-y-auto divide-y divide-slate-100">
                    {drivers.map((d) => (
                      <label key={d.id} data-testid={`bulk-driver-${d.id}`} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm">
                        <input type="checkbox" checked={bulkSelected.includes(d.id)} onChange={() => toggleBulkDriver(d.id)} className="rounded border-slate-300" />
                        <span className="text-slate-800">{d.name || d.email}</span>
                        {configs.some((c) => c.driver_id === d.id) && <span className="ml-auto text-[10px] uppercase tracking-widest text-amber-600">has escrow</span>}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <button data-testid="bulk-save-button" type="submit" disabled={savingBulk} className="bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] disabled:opacity-50">{savingBulk ? 'Applying…' : `Apply to ${bulkSelected.length} driver${bulkSelected.length === 1 ? '' : 's'}`}</button>
                  <button type="button" onClick={() => setShowBulk(false)} className="text-sm text-slate-600 hover:text-slate-900">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}
        {showBulkRefund && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowBulkRefund(false)}>
            <div data-testid="bulk-refund-modal" className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Bulk Refund</h2>
                <button onClick={() => setShowBulkRefund(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-xs text-slate-500 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Refunds each selected driver's full held escrow balance. Amounts at or above {fmtMoney(threshold)} still require a second admin's approval.</p>
              {refundableDrivers.length === 0 ? (
                <p className="text-sm text-slate-500">No drivers currently have a held escrow balance to refund.</p>
              ) : (
                <form onSubmit={saveBulkRefund} className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{bulkRefundSelected.length} selected</span>
                    <button type="button" data-testid="bulk-refund-select-all" onClick={() => setBulkRefundSelected(bulkRefundSelected.length === refundableDrivers.length ? [] : refundableDrivers.map((c) => c.driver_id))} className="text-xs text-[#002FA7] font-semibold hover:underline">
                      {bulkRefundSelected.length === refundableDrivers.length ? 'Clear all' : 'Select all'}
                    </button>
                  </div>
                  <div className="border border-slate-200 rounded-md max-h-64 overflow-y-auto divide-y divide-slate-100">
                    {refundableDrivers.map((c) => (
                      <label key={c.driver_id} data-testid={`bulk-refund-driver-${c.driver_id}`} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm">
                        <input type="checkbox" checked={bulkRefundSelected.includes(c.driver_id)} onChange={() => setBulkRefundSelected((p) => (p.includes(c.driver_id) ? p.filter((x) => x !== c.driver_id) : [...p, c.driver_id]))} className="rounded border-slate-300" />
                        <span className="text-slate-800">{c.driver_name}</span>
                        <span className="ml-auto font-semibold text-emerald-700">{fmtMoney(c.held_balance)}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <button data-testid="bulk-refund-save" type="submit" disabled={savingBulkRefund} className="bg-emerald-600 text-white px-4 py-2 rounded-md font-semibold hover:bg-emerald-700 disabled:opacity-50">{savingBulkRefund ? 'Refunding…' : `Refund ${bulkRefundSelected.length} driver${bulkRefundSelected.length === 1 ? '' : 's'}`}</button>
                    <button type="button" onClick={() => setShowBulkRefund(false)} className="text-sm text-slate-600 hover:text-slate-900">Cancel</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {refundTarget && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setRefundTarget(null)}>
            <div data-testid="refund-modal" className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Refund Escrow</h2>
                <button onClick={() => setRefundTarget(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-xs text-slate-500 mb-4">{refundTarget.driver_name} · held balance <span className="font-semibold text-emerald-700">{fmtMoney(refundTarget.held_balance)}</span>. Paid out on next payroll{refundTarget.held_balance >= threshold ? '; large refunds need a second admin.' : '.'}</p>
              <form onSubmit={submitRefund} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Amount ($)</label>
                    <input data-testid="refund-amount-input" type="number" step="0.01" min="0.01" max={refundTarget.held_balance} value={refundForm.amount} onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md" required />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Reason</label>
                    <select data-testid="refund-reason-select" value={refundForm.reason} onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white">
                      <option value="overpayment">Overpayment</option>
                      <option value="contract_end">Contract End</option>
                      <option value="adjustment">Adjustment</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Note (optional)</label>
                  <input data-testid="refund-note-input" type="text" value={refundForm.notes} onChange={(e) => setRefundForm({ ...refundForm, notes: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <button data-testid="refund-save-button" type="submit" disabled={savingRefund} className="bg-emerald-600 text-white px-4 py-2 rounded-md font-semibold hover:bg-emerald-700 disabled:opacity-50">{savingRefund ? 'Saving…' : 'Queue Refund'}</button>
                  <button type="button" onClick={() => setRefundTarget(null)} className="text-sm text-slate-600 hover:text-slate-900">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {settlement && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setSettlement(null)}>
            <div data-testid="settlement-modal" className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}><LogOut className="w-5 h-5 text-purple-600" /> Settlement Complete</h2>
                <button onClick={() => setSettlement(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-sm text-slate-600 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{settlement.driver_name}</p>
              <div className="space-y-2 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <div className="flex justify-between"><span className="text-slate-500">Held escrow</span><span className="font-semibold text-slate-900">{fmtMoney(settlement.held_balance)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Deferred debt</span><span className="font-semibold text-red-600">{fmtMoney(settlement.deferred_debt)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Debt cleared from escrow</span><span className="font-semibold text-emerald-700">{fmtMoney(settlement.debt_cleared)}</span></div>
                <div className="border-t border-slate-200 my-2" />
                {settlement.cash_refund > 0 && (
                  <div className="flex justify-between text-base"><span className="text-slate-700 font-semibold">Cash refund due</span><span className="font-bold text-emerald-700">{fmtMoney(settlement.cash_refund)}</span></div>
                )}
                {settlement.remaining_owed > 0 && (
                  <div className="flex justify-between text-base"><span className="text-slate-700 font-semibold">Balance still owed</span><span className="font-bold text-red-600">{fmtMoney(settlement.remaining_owed)}</span></div>
                )}
                {settlement.refund_needs_approval && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-2">The cash refund exceeds the approval threshold and is awaiting a second admin's sign-off.</p>
                )}
              </div>
              <div className="flex items-center gap-2 pt-4">
                <button data-testid="download-statement-button" onClick={() => downloadStatement(settlement)} className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D]"><FileText className="w-4 h-4" /> Statement</button>
                <button data-testid="download-packet-button" onClick={() => downloadPacket(settlement)} className="flex items-center gap-2 bg-white border border-[#002FA7] text-[#002FA7] px-4 py-2 rounded-md font-semibold hover:bg-blue-50"><FileText className="w-4 h-4" /> Off-Boarding Packet</button>
                <button onClick={() => setSettlement(null)} className="text-sm text-slate-600 hover:text-slate-900">Close</button>
              </div>
            </div>
          </div>
        )}
        {correcting && (
          <EscrowCorrectionModal
            row={correcting.row}
            driverName={correcting.driverName}
            onClose={() => setCorrecting(null)}
            onCorrected={(data) => {
              const did = correcting.row.driver_id;
              if (data.draft) {
                setLedgerByDriver((p) => { const n = { ...p }; delete n[did]; return n; });
                axios.get(`${API}/escrow/ledger?driver_id=${did}`, { withCredentials: true }).then((res) => setLedgerByDriver((p) => ({ ...p, [did]: res.data }))).catch(() => {});
              } else {
                setLedgerByDriver((p) => ({ ...p, [did]: (p[did] || []).map((x) => (x.id === data.ledger.id ? data.ledger : x)) }));
              }
              setCorrecting(null);
              fetchData();
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default EscrowManagement;
